import React, { useEffect, useRef, useState } from "react";
import "./loader.css";

interface LoaderScreenProps {
  onComplete?: () => void;
  /** Total run-time in ms — 1 s blackout + 7 s video = 8 s. */
  duration?: number;
  /** Skip the sessionStorage first-visit guard (dev / preview). */
  force?: boolean;
}

/**
 * IDEATION AV — 8-second cinematic loader.
 *
 * 0.0 – 1.0 s   : total blackout (video is preloaded but hidden)
 * 1.0 – 8.0 s   : 7-second video plays (loading.mp4, 1280×720, 16:9)
 * 8.0 s         : 220 ms hero-hold at 100 %, then 700 ms dissolve into the site
 *
 * Progress bar animates 0 → 100 % from 1 s to 8 s so the % is synchronised
 * with the video, never landing at 100 % ahead of the final frame.
 *
 * IDEATION lockup renders from a single static SVG — one image, one DOM node,
 * no async/conditional swap, no flicker.
 * First-visit-only via sessionStorage.
 */

const VIDEO_SRC = "/loader/loading.mp4";
const BLACKOUT_MS = 1000;
const VIDEO_MS = 7000;

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

const LoaderScreen: React.FC<LoaderScreenProps> = ({ onComplete, duration = BLACKOUT_MS + VIDEO_MS, force = false }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number | null>(null);
  const [progress, setProgress] = useState(0);
  const [videoStarted, setVideoStarted] = useState(false);
  const [fadingOut, setFadingOut] = useState(false);
  const [mounted, setMounted] = useState(true);

  // First-visit guard.
  useEffect(() => {
    if (force) return;
    const seen = typeof window !== "undefined" && window.sessionStorage.getItem("iav_loader_seen");
    if (seen === "1") {
      setMounted(false);
      onComplete?.();
    }
  }, [force, onComplete]);

  // RAF tick — drives blackout gating, video kickoff, and progress.
  useEffect(() => {
    if (!mounted) return;

    const tick = (ts: number) => {
      if (startRef.current == null) startRef.current = ts;
      const elapsed = ts - startRef.current;

      // Kick off video playback exactly at the 1 s blackout boundary. Autoplay
      // is muted so browsers permit it without user interaction.
      if (elapsed >= BLACKOUT_MS && !videoStarted) {
        setVideoStarted(true);
        const v = videoRef.current;
        if (v) {
          v.currentTime = 0;
          const p = v.play();
          if (p && typeof p.catch === "function") p.catch(() => { /* still show the frame */ });
        }
      }

      // Progress: pinned at 0 through the blackout, then linear ramp to 100 %
      // across the 7-second video (with a subtle ease-out so it doesn't hit
      // 100 % before the last video frame).
      let pct = 0;
      if (elapsed >= BLACKOUT_MS) {
        const local = Math.min(1, (elapsed - BLACKOUT_MS) / VIDEO_MS);
        const eased = 1 - Math.pow(1 - local, 1.5);   // gentle ease-out
        pct = Math.round(lerp(0, 100, eased));
      }
      setProgress(pct);

      if (elapsed < duration) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setProgress(100);
        // Hold the final frame ~220 ms, then dissolve to the site.
        setTimeout(() => {
          setFadingOut(true);
          setTimeout(() => {
            try { window.sessionStorage.setItem("iav_loader_seen", "1"); } catch { /* private mode */ }
            setMounted(false);
            onComplete?.();
          }, 700);
        }, 220);
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [mounted, duration, videoStarted, onComplete]);

  if (!mounted) return null;

  return (
    <div
      className={`iav-loader ${fadingOut ? "is-fading" : ""}`}
      role="status"
      aria-live="polite"
      aria-label={`Loading Ideation Audio Visual — ${progress} percent`}
      data-testid="loader-screen"
    >
      {/* Stage — centred 16:9 container. Full-frame video, no crop. */}
      <div className="iav-loader-stage" aria-hidden data-testid="loader-stage">
        <video
          ref={videoRef}
          className={`iav-loader-video ${videoStarted ? "is-visible" : ""}`}
          src={VIDEO_SRC}
          muted
          playsInline
          preload="auto"
          data-testid="loader-video"
        />
      </div>

      {/* HUD — single static IDEATION lockup + LOADING… + animated bar. */}
      <div className="iav-loader-hud" data-testid="loader-hud">
        <div className="iav-loader-lockup" data-testid="loader-lockup">
          <img
            src="/loader/ideation-logo.svg"
            alt="Ideation AV"
            className="iav-loader-lockup-img"
            width={288}
            height={78}
          />
        </div>
        <div className="iav-loader-strap-hero">
          DESIGNING <span className="iav-blue">EXPERIENCES.</span> DELIVERING <span className="iav-blue">IMPACT.</span>
        </div>
        <div className="iav-loader-loading">LOADING…</div>
        <div
          className="iav-loader-track"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progress}
        >
          <div className="iav-loader-bar" style={{ width: `${progress}%` }} />
          <div className="iav-loader-bar-glow" style={{ left: `${progress}%` }} />
        </div>
        <div className="iav-loader-row">
          <span className="iav-loader-strap">PREPARING YOUR AV EXPERIENCE</span>
          <span className="iav-loader-pct" data-testid="loader-pct">{progress}%</span>
        </div>
      </div>
    </div>
  );
};

export default LoaderScreen;
